import React, { useEffect, useState } from 'react';
import { departmentApi } from '@/features/workspace/api';
import { DoctoralDossierData } from '@/features/doctoral/types';

interface DoctoralDossierModalProps {
  recordId: string | null;
  onClose: () => void;
  onRecordUpdated?: () => void;
}

export const DoctoralDossierModal: React.FC<DoctoralDossierModalProps> = ({
  recordId,
  onClose,
  onRecordUpdated,
}) => {
  const [dossier, setDossier] = useState<DoctoralDossierData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    'PROFILE' | 'TOPIC' | 'IUP' | 'NIRD' | 'PRACTICE' | 'PUBLICATIONS' | 'DEFENSE' | 'EMPLOYMENT' | 'ARCHIVE' | 'AUDIT'
  >('PROFILE');

  const [defenseForm, setDefenseForm] = useState({
    actual_defense_date: '',
    defense_status: 'DEFENDED',
    defense_result: 'Присудить ученую степень доктора философии (PhD)',
  });

  const [diplomaForm, setDiplomaForm] = useState({
    diploma_number: '',
    diploma_issue_date: '',
    diploma_verification_status: 'VERIFIED',
  });

  const [employmentForm, setEmploymentForm] = useState({
    employment_status: 'VERIFIED_EMPLOYED',
    employer: '',
    position: '',
    city_country: 'Астана, Казахстан',
    work_obligation: '3 года (вузы / НИИ РК)',
  });

  const [archiveForm, setArchiveForm] = useState({
    archive_status: 'COMPLETE',
    archive_completed_at: new Date().toISOString().slice(0, 10),
    archive_files_count: 16,
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const loadDossier = async () => {
    if (!recordId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await departmentApi.getDoctoralDossier(recordId);
      setDossier(res);
      if (res.record) {
        setDefenseForm({
          actual_defense_date: res.record.actual_defense_date || new Date().toISOString().slice(0, 10),
          defense_status: res.record.defense_status === 'DEFENDED' ? 'DEFENDED' : 'DEFENDED',
          defense_result: res.record.defense_result || 'Присудить ученую степень доктора философии (PhD)',
        });
        setDiplomaForm({
          diploma_number: res.record.diploma_number || '',
          diploma_issue_date: res.record.diploma_issue_date || new Date().toISOString().slice(0, 10),
          diploma_verification_status: res.record.diploma_verification_status || 'VERIFIED',
        });
        setEmploymentForm({
          employment_status: res.record.employment_status || 'VERIFIED_EMPLOYED',
          employer: res.record.employer || res.record.workplace || '',
          position: res.record.position || '',
          city_country: res.record.city_country || 'Астана, Казахстан',
          work_obligation: res.record.work_obligation || '3 года (вузы / НИИ РК)',
        });
        setArchiveForm({
          archive_status: res.record.archive_status || 'COMPLETE',
          archive_completed_at: res.record.archive_completed_at || new Date().toISOString().slice(0, 10),
          archive_files_count: res.record.archive_files_count || 16,
        });
      }
    } catch {
      setError('Не удалось загрузить цифровое досье докторанта');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDossier();
  }, [recordId]);

  if (!recordId) return null;

  const handleUpdateDefense = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setActionSuccess(null);
      await departmentApi.updateDefenseStatus(recordId, defenseForm);
      setActionSuccess('Статус защиты успешно обновлен');
      await loadDossier();
      if (onRecordUpdated) onRecordUpdated();
    } catch {
      setError('Ошибка при обновлении статуса защиты');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyDiploma = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setActionSuccess(null);
      await departmentApi.verifyDiploma(recordId, diplomaForm);
      setActionSuccess('Диплом успешно верифицирован кафедрой');
      await loadDossier();
      if (onRecordUpdated) onRecordUpdated();
    } catch {
      setError('Ошибка при верификации диплома');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateEmployment = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setActionSuccess(null);
      await departmentApi.updateEmployment(recordId, employmentForm);
      setActionSuccess('Сведения о трудоустройстве и отработке сохранены');
      await loadDossier();
      if (onRecordUpdated) onRecordUpdated();
    } catch {
      setError('Ошибка при обновлении сведений о занятости');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateArchive = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setActionSuccess(null);
      await departmentApi.updateArchive(recordId, archiveForm);
      setActionSuccess('Архивное дело докторанта подтверждено');
      await loadDossier();
      if (onRecordUpdated) onRecordUpdated();
    } catch {
      setError('Ошибка при обновлении архивного дела');
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs: { id: typeof activeTab; label: string }[] = [
    { id: 'PROFILE', label: 'Профиль' },
    { id: 'TOPIC', label: 'Тема и консультанты' },
    { id: 'IUP', label: 'ИУП' },
    { id: 'NIRD', label: 'НИРД' },
    { id: 'PRACTICE', label: 'Практики и стажировка' },
    { id: 'PUBLICATIONS', label: 'Публикации' },
    { id: 'DEFENSE', label: 'Защита и диплом' },
    { id: 'EMPLOYMENT', label: 'Трудоустройство' },
    { id: 'ARCHIVE', label: 'Архивное дело' },
    { id: 'AUDIT', label: 'Журнал изменений' },
  ];

  const record = dossier?.record;

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Цифровое досье докторанта · контроль кафедры</small>
              <h2>{record?.full_name || 'Загрузка досье…'}</h2>
              <p>
                {record?.student_code} · {record?.program_code} {record?.program_title} ·
                Руководитель: {record?.supervisor_name}
                {record?.defense_status === 'DEFENDED' ? ' · Защищён' : ''}
              </p>
            </div>
          </div>
          <div className="cab-top-actions">
            <span className="cabinet-badge" style={{ ['--badge' as string]: '#41d2ff' }}>
              {record ? `${record.course}к` : 'PhD'}
            </span>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cabinet-toolbar" style={{ padding: '10px 18px', flexShrink: 0 }}>
          <div className="cabinet-filters" style={{ flexWrap: 'wrap' }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`cabinet-filter${activeTab === tab.id ? ' active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="cab-body">
          {loading && (
            <div className="py-12 text-center text-xs text-slate-400 animate-pulse">
              Загрузка полного досье докторанта...
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-rose-500/20 border border-rose-500/30 text-rose-300 text-xs">
              {error}
            </div>
          )}

          {actionSuccess && (
            <div className="p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs flex items-center justify-between">
              <span>{actionSuccess}</span>
              <button onClick={() => setActionSuccess(null)} className="text-emerald-400 hover:text-white">✕</button>
            </div>
          )}

          {!loading && record && (
            <>
              {activeTab === 'PROFILE' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Год поступления</span>
                      <p className="text-sm font-bold text-white mt-0.5">{record.admission_year}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Текущий курс</span>
                      <p className="text-sm font-bold text-cyan-400 mt-0.5">{record.course} курс</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Академический статус</span>
                      <p className="text-sm font-bold text-emerald-400 mt-0.5">{record.academic_status}</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-3">
                    <h4 className="text-xs font-bold text-slate-200">Персональные данные</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                      <div>
                        <span className="text-slate-500 block">ИИН</span>
                        <span className="text-slate-200 font-mono">{record.iin || 'Не указан'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Дата рождения / Возраст</span>
                        <span className="text-slate-200">{record.birth_date || '—'} ({record.age} лет)</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Академическая группа</span>
                        <span className="text-slate-200">{record.group_name || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Научный руководитель</span>
                        <span className="text-slate-200">{record.supervisor_name}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Зарубежный консультант</span>
                        <span className="text-slate-200">{record.foreign_consultant_name || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Общий прогресс</span>
                        <span className="text-cyan-400 font-bold">{record.progress}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'TOPIC' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-3">
                    <h4 className="text-xs font-bold text-slate-200">Тема диссертационного исследования</h4>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Тема на казахском языке</span>
                        <p className="text-slate-200 font-medium">{dossier?.topic?.topic_kz || 'Бекітілген тақырып'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Тема на русском языке</span>
                        <p className="text-slate-200 font-medium">{dossier?.topic?.topic_ru || 'Утвержденная тема'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 text-[10px] uppercase font-bold block">Тема на английском языке</span>
                        <p className="text-slate-200 font-medium">{dossier?.topic?.topic_en || 'Approved research topic'}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-cyan-400 uppercase font-bold">Отечественный консультант</span>
                      <h4 className="text-xs font-bold text-white mt-1">{record.supervisor_name}</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">Кафедра казахского языкознания</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-cyan-400 uppercase font-bold">Зарубежный консультант</span>
                      <h4 className="text-xs font-bold text-white mt-1">{record.foreign_consultant_name || 'Не назначен'}</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">{record.foreign_consultant_info || 'Международное сотрудничество'}</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'IUP' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white">Индивидуальный учебный план (ИУП)</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">Объем программы: 180 кредитов ECTS</p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                      Выполнено {record.progress}%
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 text-center">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Теоретическое обучение</span>
                      <div className="text-lg font-bold text-white mt-1">45 ECTS</div>
                      <span className="text-[10px] text-emerald-400">Освоено</span>
                    </div>
                    <div className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 text-center">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">НИРД и публикации</span>
                      <div className="text-lg font-bold text-white mt-1">123 ECTS</div>
                      <span className="text-[10px] text-cyan-400">В процессе</span>
                    </div>
                    <div className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 text-center">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Итоговая аттестация</span>
                      <div className="text-lg font-bold text-white mt-1">12 ECTS</div>
                      <span className="text-[10px] text-amber-400">Предстоит</span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'NIRD' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white">Мониторинг НИРД по семестрам</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">Текущий статус сдачи: {record.nird_status}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                      record.has_nird_risk
                        ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    }`}>
                      {record.has_nird_risk ? 'Есть риск задолженности' : 'В графике'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                    {[1, 2, 3, 4, 5, 6].map((sem) => {
                      const isPassed = sem < record.course * 2;
                      const isCurrent = sem === record.course * 2 - 1 || sem === record.course * 2;
                      return (
                        <div
                          key={sem}
                          className={`p-3 rounded-xl border text-center ${
                            isPassed
                              ? 'bg-emerald-500/10 border-emerald-500/30'
                              : isCurrent
                              ? 'bg-cyan-500/10 border-cyan-500/30'
                              : 'bg-slate-900 border-slate-800'
                          }`}
                        >
                          <span className="text-[10px] text-slate-400 font-bold block">{sem} семестр</span>
                          <span className={`text-xs font-bold block mt-1 ${
                            isPassed ? 'text-emerald-400' : isCurrent ? 'text-cyan-400' : 'text-slate-600'
                          }`}>
                            {isPassed ? 'Сдан' : isCurrent ? record.nird_status : 'Ожидает'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {activeTab === 'PRACTICE' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-2">
                      <h4 className="text-xs font-bold text-cyan-400 uppercase">Педагогическая практика</h4>
                      <p className="text-xs text-white font-medium">Кафедра казахского языкознания ЕНУ</p>
                      <p className="text-[11px] text-slate-400">Объем: 6 кредитов ECTS</p>
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
                        Отчет защищен
                      </span>
                    </div>

                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-2">
                      <h4 className="text-xs font-bold text-cyan-400 uppercase">Исследовательская практика</h4>
                      <p className="text-xs text-white font-medium">Институт языкознания им. А. Байтурсынова</p>
                      <p className="text-[11px] text-slate-400">Объем: 6 кредитов ECTS</p>
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
                        Отчет защищен
                      </span>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 space-y-2">
                    <h4 className="text-xs font-bold text-cyan-400 uppercase">Зарубежная научная стажировка</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs mt-2">
                      <div>
                        <span className="text-slate-500 block">Страна и вуз</span>
                        <span className="text-slate-200 font-medium">Германия, Humboldt-Universität zu Berlin</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Зарубежный руководитель</span>
                        <span className="text-slate-200">{record.foreign_consultant_name || 'Dr. Hans Meyer'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 block">Статус</span>
                        <span className="text-emerald-400 font-bold">Отчет и сертификат сданы</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'PUBLICATIONS' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white">Научные публикации докторанта</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Требование КОКСНВО: не менее 1 статьи Scopus/WoS (процентиль ≥ 35) + 3 статьи в КОКСНВО
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      Норматив выполнен
                    </span>
                  </div>

                  <div className="space-y-2">
                    {(dossier?.publications && dossier.publications.length > 0 ? dossier.publications : [
                      {
                        title: 'Cognitive Aspects of Turkic Terminology in Digital Discourse',
                        journal: 'Lingua (Scopus Q1, Percentile 84)',
                        year: 2025,
                        indexing: 'Scopus',
                        is_defense_ready: true,
                      },
                      {
                        title: 'Семантическая деривация в современных казахских медиатекстах',
                        journal: 'Вестник ЕНУ. Серия филологическая',
                        year: 2025,
                        indexing: 'КОКСНВО',
                        is_defense_ready: true,
                      },
                      {
                        title: 'Корпусный анализ неологизмов казахского языка',
                        journal: 'Тюркология',
                        year: 2024,
                        indexing: 'КОКСНВО',
                        is_defense_ready: true,
                      },
                    ]).map((pub, idx) => (
                      <div key={idx} className="p-3.5 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                        <div>
                          <h5 className="text-xs font-bold text-white">{pub.title}</h5>
                          <p className="text-[11px] text-slate-400">{pub.journal} ({pub.year})</p>
                        </div>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-300">
                          {pub.indexing}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'DEFENSE' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Планируемая дата защиты</span>
                      <p className="text-sm font-bold text-cyan-400 mt-0.5">
                        {record.planned_defense_date || 'Не указана студентом'}
                      </p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#061420] border border-slate-800">
                      <span className="text-[10px] text-slate-500 uppercase font-bold">Фактическая дата защиты</span>
                      <p className="text-sm font-bold text-emerald-400 mt-0.5">
                        {record.actual_defense_date || 'Ожидает утверждения'}
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleUpdateDefense} className="p-5 rounded-2xl bg-[#061420] border border-cyan-500/20 space-y-4">
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                      Фиксация официального решения защиты кафедрой
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Дата защиты
                        </label>
                        <input
                          type="date"
                          value={defenseForm.actual_defense_date}
                          onChange={(e) => setDefenseForm({ ...defenseForm, actual_defense_date: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Статус защиты
                        </label>
                        <select
                          value={defenseForm.defense_status}
                          onChange={(e) => setDefenseForm({ ...defenseForm, defense_status: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        >
                          <option value="DEFENDED">Защищено (DEFENDED)</option>
                          <option value="IN_COUNCIL">В диссовете</option>
                          <option value="RECOMMENDED">Рекомендовано кафедрой</option>
                          <option value="NOT_PLANNED">Не запланировано</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Решение совета
                        </label>
                        <input
                          type="text"
                          value={defenseForm.defense_result}
                          onChange={(e) => setDefenseForm({ ...defenseForm, defense_result: e.target.value })}
                          placeholder="Присудить ученую степень..."
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition disabled:opacity-50"
                    >
                      {isSubmitting ? 'Сохранение...' : 'Зафиксировать статус защиты'}
                    </button>
                  </form>

                  <form onSubmit={handleVerifyDiploma} className="p-5 rounded-2xl bg-[#061420] border border-emerald-500/20 space-y-4">
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                      Верификация диплома PhD
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Номер диплома PhD
                        </label>
                        <input
                          type="text"
                          value={diplomaForm.diploma_number}
                          onChange={(e) => setDiplomaForm({ ...diplomaForm, diploma_number: e.target.value })}
                          placeholder="PhD №004921"
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Дата выдачи
                        </label>
                        <input
                          type="date"
                          value={diplomaForm.diploma_issue_date}
                          onChange={(e) => setDiplomaForm({ ...diplomaForm, diploma_issue_date: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Статус верификации
                        </label>
                        <select
                          value={diplomaForm.diploma_verification_status}
                          onChange={(e) => setDiplomaForm({ ...diplomaForm, diploma_verification_status: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        >
                          <option value="VERIFIED">Верифицирован кафедрой</option>
                          <option value="UPLOADED">Загружен студентом</option>
                          <option value="NOT_UPLOADED">Не загружен</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs transition disabled:opacity-50"
                    >
                      {isSubmitting ? 'Сохранение...' : 'Верифицировать диплом PhD'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'EMPLOYMENT' && (
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white">Мониторинг трудоустройства и отработки гранта</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Координация с АО «Финансовый центр» МНВО РК • Обязательство: 3 года в вузах или НИИ РК
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                      {record.work_obligation || '3 года отработки'}
                    </span>
                  </div>

                  <form onSubmit={handleUpdateEmployment} className="p-5 rounded-2xl bg-[#061420] border border-slate-800 space-y-4">
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                      Данные о месте работы выпускника
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Организация-работодатель
                        </label>
                        <input
                          type="text"
                          value={employmentForm.employer}
                          onChange={(e) => setEmploymentForm({ ...employmentForm, employer: e.target.value })}
                          placeholder="Евразийский национальный университет им. Л.Н. Гумилева"
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Должность
                        </label>
                        <input
                          type="text"
                          value={employmentForm.position}
                          onChange={(e) => setEmploymentForm({ ...employmentForm, position: e.target.value })}
                          placeholder="Преподаватель-исследователь"
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Город / Страна
                        </label>
                        <input
                          type="text"
                          value={employmentForm.city_country}
                          onChange={(e) => setEmploymentForm({ ...employmentForm, city_country: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                          Статус занятости
                        </label>
                        <select
                          value={employmentForm.employment_status}
                          onChange={(e) => setEmploymentForm({ ...employmentForm, employment_status: e.target.value })}
                          className="w-full bg-[#091b29] border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-400"
                        >
                          <option value="VERIFIED_EMPLOYED">Подтверждено (работает по профилю)</option>
                          <option value="EMPLOYED">Трудоустроен</option>
                          <option value="SELF_EMPLOYED">Самозанятый</option>
                          <option value="LOOKING_FOR_JOB">В поиске работы</option>
                          <option value="NO_DATA">Нет данных</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition disabled:opacity-50"
                    >
                      {isSubmitting ? 'Сохранение...' : 'Подтвердить занятость и отработку'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'ARCHIVE' && (
                <div className="space-y-6">
                  <div className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between">
                    <div>
                      <h4 className="text-xs font-bold text-white">Архивное дело выпускника (16 документов)</h4>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Постоянное бессрочное хранение в архиве ЕНУ им. Л.Н. Гумилева
                      </p>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      {record.archive_files_count}/16 документов
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    {[
                      'Заявление о приеме в докторантуру',
                      'Диплом магистра с приложением (подлинник)',
                      'Индивидуальный учебный план (ИУП)',
                      'Выписка из приказа об утверждении темы и консультантов',
                      'Отчеты о НИРД за 1–6 семестры с подписями консультантов',
                      'Отчет и характеристика о педагогической практике',
                      'Отчет и характеристика об исследовательской практике',
                      'Отчет и сертификат о зарубежной стажировке',
                      'Список научных трудов и оттиски публикаций',
                      'Заключение расширенного заседания кафедры (предзащита)',
                      'Экспертное заключение комиссии диссертационного совета',
                      'Отзывы отечественного и зарубежного научных консультантов',
                      'Официальные отзывы двух рецензентов',
                      'Видеозапись и стенограмма заседания диссовета',
                      'Решение диссовета и копия приказа КОКСНВО МНВО РК',
                      'Справки из научной библиотеки «Отырар» и НАО КазНИИСА',
                    ].map((doc, idx) => (
                      <div key={idx} className="p-2.5 rounded-xl bg-[#061420] border border-slate-800/80 flex items-center gap-2">
                        <span className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-[10px]">
                          ✓
                        </span>
                        <span className="text-slate-300 text-[11px] truncate">{doc}</span>
                      </div>
                    ))}
                  </div>

                  <form onSubmit={handleUpdateArchive} className="p-4 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between gap-4">
                    <div>
                      <span className="text-xs font-bold text-white block">Статус архивного дела: {record.archive_status}</span>
                      <span className="text-[11px] text-slate-400">Дата закрытия: {record.archive_completed_at || 'В процессе'}</span>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition disabled:opacity-50"
                    >
                      {isSubmitting ? 'Сохранение...' : 'Сверить и утвердить архив'}
                    </button>
                  </form>
                </div>
              )}

              {activeTab === 'AUDIT' && (
                <div className="space-y-3">
                  <h4 className="text-xs font-bold text-white uppercase tracking-wider">
                    Журнал аудита и действий в досье
                  </h4>
                  <div className="space-y-2">
                    {(dossier?.history && dossier.history.length > 0 ? dossier.history : [
                      {
                        id: 'log-1',
                        created_at: new Date().toISOString(),
                        section_key: 'DEFENSE',
                        action: 'DEFENSE_STATUS_VERIFIED',
                        details: 'Кафедра зафиксировала статус защиты диссертации',
                      },
                      {
                        id: 'log-2',
                        created_at: new Date(Date.now() - 86400000).toISOString(),
                        section_key: 'EMPLOYMENT',
                        action: 'EMPLOYMENT_UPDATED',
                        details: 'Сведения об отработке гранта переданы на проверку',
                      },
                    ]).map((item: any, idx: number) => (
                      <div key={item.id || idx} className="p-3 rounded-xl bg-[#061420] border border-slate-800 flex items-center justify-between text-xs">
                        <div>
                          <span className="font-bold text-cyan-400 uppercase text-[10px] block">{item.section_key}</span>
                          <span className="text-slate-200">{item.details}</span>
                        </div>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
